import React, { useEffect } from "react";
import { AnalysisTreeProto, AnalysisType } from "build-ts/analysis";
import { useAnalysisTreeContext } from "../../context/AnalysisTreeContext";
import TreeVisualization from "../../components/analyse/TreeVisualization";
import { TreeNode } from "../../utils/treeUtils";
import { Box } from "@chakra-ui/react";
import { getAnalysisNodeName } from "utils/analysisUtils";

interface TreePanelContainerProps {
  tree: AnalysisTreeProto | null;
  isChatOpen: boolean;
  activeDataTables: Map<string, number>;
  onNodeSelect: (nodeId: string) => void;
  isClearAllTables: boolean;
  setIsClearAllTables: (isClearAllTables: boolean) => void;
}

const TreePanelContainer: React.FC<TreePanelContainerProps> = ({ tree, isChatOpen, onNodeSelect, activeDataTables, isClearAllTables, setIsClearAllTables }) => {
  const {
    getNodeData,
    commentaryNodes,
    setCommentaryNodes,
    nodeInfoMap,
    currentSelectedNode
  } = useAnalysisTreeContext();

  // Track previous tree for comparison
  const prevTreeRef = React.useRef<AnalysisTreeProto | null>(null);

  const rootNodeSelectedRef = React.useRef<boolean>(false);
  
  // Convert AnalysisTreeProto to TreeNode format
  const convertToTreeData = (nodeId: string, parentId?: string): TreeNode | null => {
    if (!tree || !nodeInfoMap) return null;
    
    const node = nodeInfoMap.get(nodeId);
    if (!node) return null;

    // Get the basic node name
    let nodeName = getAnalysisNodeName(node.current_node, tree);
    
    // If this node has a parent, find the edge label to add to the node name
    if (parentId) {
      const parentNode = nodeInfoMap.get(parentId);
      if (parentNode) {
        let edgeLabel = "";
        
        // Handle DRIVER_ANALYSIS parent
        if (parentNode.current_node.analysis_type === AnalysisType.DRIVER_ANALYSIS) {
          const nodeFilterHeaders = new Set(
            parentNode.current_node.observation?.content?.filters.map(
              (filter) => filter?.logical_column_header?.id
            )
          );

          const uniqueFilters = node.current_node.observation?.content?.filters?.filter(
            (filter) => !nodeFilterHeaders.has(filter?.logical_column_header?.id)
          );

          edgeLabel = uniqueFilters?.map((filter) => {
            return `${filter.values && filter.values.join(", ")}`;
          }).join(", ") || "";

          if (edgeLabel === "") {
            edgeLabel = "No unique filters";
          }
          
          edgeLabel = `For ${edgeLabel},`;
        } 
        // Handle CORRELATION_ANALYSIS parent
        else if (parentNode.current_node.analysis_type === AnalysisType.CORRELATION_ANALYSIS) {
          const observationColumns = node.current_node.observation?.content?.observation_columns;

          edgeLabel = observationColumns?.map((column) => column?.logical_column_header?.name).join(", ") || "";

          if (edgeLabel === "") {
            edgeLabel = "No label is present";
          }
          
          edgeLabel = `For ${edgeLabel},`;
        }
        
        // Add edge label to node name if it exists
        if (edgeLabel) {
          nodeName = `${edgeLabel} ${nodeName}`;
        }
      }
    }

    const children = node.current_node.children_ids?.map(childId => 
      convertToTreeData(childId, nodeId)  // Pass current node as parent
    ).filter((child): child is TreeNode => child !== null);

    return {
      name: node.current_node.node_label ? node.current_node.node_label : nodeName,
      children: children?.length ? children : undefined,
      id: nodeId,
    };
  };

  // Generate tree data from the root node
  const generateTreeData = (): TreeNode | null => {
    if (!tree || !nodeInfoMap || !tree.root_node_id) return null;
    return convertToTreeData(tree.root_node_id);  // Root node has no parent
  };

  const handleNodeClick = (nodeId: string, shiftKey: boolean) => {
    if (!nodeInfoMap) return;

    if (shiftKey) {
      const newSelectedNodes = new Set(commentaryNodes);
      if (newSelectedNodes.has(nodeId)) {
        newSelectedNodes.delete(nodeId);
      } else {
        newSelectedNodes.add(nodeId);
      }
      setCommentaryNodes(newSelectedNodes);
    } else {
      const clickedNode = nodeInfoMap.get(nodeId);
      if (clickedNode?.current_node?.id) {
        currentSelectedNode.current = clickedNode.current_node.id;
        getNodeData(clickedNode.current_node.id);  // Single call

        onNodeSelect(clickedNode.current_node.id);
      }
    }
  };

  // Effect to detect new nodes and automatically load their data
  useEffect(() => {
    if (!tree || !prevTreeRef.current || !nodeInfoMap) {
      prevTreeRef.current = tree;
      return;
    }

    // Check if there are new nodes in the tree
    if (tree.nodes.length > prevTreeRef.current.nodes.length) {
      // Get the most recent node added (assuming new nodes are added to the end)
      const newNode = tree.nodes[tree.nodes.length - 1];
      
      if (newNode?.id) {
        // Automatically load data for the new node
        currentSelectedNode.current = newNode.id;
        getNodeData(newNode.id);

        onNodeSelect(newNode.id);
      }
    }

    // Update the ref to the current tree for future comparisons
    prevTreeRef.current = tree;
  }, [tree, nodeInfoMap, getNodeData]);

  const treeDataConverted = generateTreeData();

  // Effect to automatically select the root node when the tree first loads
  useEffect(() => {
    if (tree && tree.root_node_id && nodeInfoMap && !rootNodeSelectedRef.current) {
      const rootNodeId = tree.root_node_id;
      const rootNode = nodeInfoMap.get(rootNodeId);
      
      if (rootNode?.current_node?.id) {
        // Set the current selected node
        currentSelectedNode.current = rootNodeId;
        
        // Load the node data
        getNodeData(rootNodeId);
        
        // Notify parent component about selection
        onNodeSelect(rootNodeId);
        
        // Mark that we've selected the root node
        rootNodeSelectedRef.current = true;
      }
    }
  }, [tree, nodeInfoMap, getNodeData, onNodeSelect]);

  // Reset the root node selection flag when tree changes
  useEffect(() => {
    if (prevTreeRef.current?.header?.id !== tree?.header?.id) {
      rootNodeSelectedRef.current = false;
    }
  }, [tree?.header?.id]);

  return (
    <Box 
      height="100%" 
      width="100%"
      position="relative"
    >
      {treeDataConverted && (
        <TreeVisualization 
          data={treeDataConverted}
          onNodeClick={handleNodeClick}
          isChatOpen={isChatOpen}
          activeDataTables={activeDataTables}
          isClearAllTables={isClearAllTables}
          setIsClearAllTables={setIsClearAllTables}
        />
      )}
    </Box>
  );
};

export default TreePanelContainer;